import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDetailPanelTileRemoval } from '@/components/shared';

export interface DashboardMetricTileProps {
  title: string;
  value: string;
  description: string;
  detail?: string;
  linkTo?: string;
  editing: boolean;
  tileId?: string;
  onRemove?: () => void;
  children?: React.ReactNode;
}

export function DashboardMetricTile({
  title,
  value,
  description,
  detail,
  linkTo,
  editing,
  tileId,
  onRemove,
  children,
}: DashboardMetricTileProps) {
  const removeFromGrid = useDetailPanelTileRemoval(tileId ?? '');
  const body = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="tile-drag-header relative flex min-h-12 items-center justify-center py-3">
        <h2 className="min-w-0 truncate text-center font-inter-tight text-xl font-semibold text-neutral-900">
          {title}
        </h2>
        {editing ? (
          <button
            type="button"
            onClick={onRemove ?? removeFromGrid}
            className="no-tile-drag focus-ring absolute right-0 top-3 rounded-sm p-1 text-neutral-400 hover:bg-red-50 hover:text-red-700"
            aria-label={`Remove ${title}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 text-center">
        <span className="group/metric relative inline-flex cursor-help font-inter-tight text-4xl font-semibold leading-none tabular-nums text-backoffice-500">
          {value}
          <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-72 -translate-x-1/2 rounded-md bg-neutral-900 p-2 text-left font-sans text-small font-normal leading-snug text-white shadow-lg group-hover/metric:block">
            {description}
          </span>
        </span>
        {detail ? (
          <p className="text-small font-semibold leading-tight text-neutral-600">{detail}</p>
        ) : null}
        {children}
      </div>
    </div>
  );

  const className = [
    'block h-full rounded-lg border border-neutral-200 bg-white p-5 shadow-sm transition hover:border-backoffice-200 hover:shadow',
    editing ? 'dashboard-metric-tile--edit-mode' : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (!editing && linkTo)
    return (
      <Link to={linkTo} className={className}>
        {body}
      </Link>
    );
  return <section className={className}>{body}</section>;
}
